import { logger } from "@/lib/logger";

import { ALLOWED_IMAGE_HOSTS } from "./image-config";

/**
 * Whether `next/image` can safely optimize this URL.
 *
 * Next 16 throws at render (and takes the whole page down) when given a remote
 * URL whose host isn't allow-listed in `images.remotePatterns`. Components use
 * this guard to decide between rendering `next/image` and falling back to their
 * placeholder — treating an unknown host exactly like "no image".
 *
 * Returns true only for `https://` URLs whose hostname is in
 * {@link ALLOWED_IMAGE_HOSTS}. Anything else — non-https, unknown host, null,
 * or a malformed URL — returns false.
 */
export function isOptimizableImageSrc(
  url: string | null | undefined,
): boolean {
  if (!url) return false;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  if (parsed.protocol !== "https:") return false;

  const isAllowed = (ALLOWED_IMAGE_HOSTS as readonly string[]).includes(parsed.hostname);
  if (!isAllowed) reportRejectedHost(parsed.hostname);

  return isAllowed;
}

/** Hosts already reported, so a grid of sixty pieces does not emit sixty lines. */
const reportedHosts = new Set<string>();

/**
 * Says, once per host, that an image host was turned away.
 *
 * **Why this exists.** The rejection is silent by design: every component treats an unknown host
 * as "no image" and draws its placeholder, which is right for the person looking at the screen
 * and wrong for whoever has to explain it. Twice a bucket changed and the photographs simply
 * vanished — the v2 bucket in 2026-09, the CDN base URL in 2026-10 — and both times the cause
 * took archaeology to find. One line would have answered it.
 *
 * **Server-side only, on purpose.** `architecture-standards.md` § Logging is explicit: nothing is
 * logged from the browser. Most of these surfaces render on the server, so that is where the
 * line appears; a host rejected only inside a client component stays silent, which is the price
 * of keeping the rule.
 *
 * **Not dev-only.** A hostname is not PII, and both occurrences were on **staging**, which is
 * where a silent failure costs most. Never user-visible and never fatal: this is a `warn` on the
 * server's stdout, `logger` swallows its own failures, and nothing here can stop an image from
 * rendering.
 */
function reportRejectedHost(hostname: string): void {
  if (typeof window !== "undefined") return;
  if (reportedHosts.has(hostname)) return;

  reportedHosts.add(hostname);
  logger.warn("images", "Image host is not allow-listed; the placeholder is drawn instead.", {
    details: { hostname, allowed: ALLOWED_IMAGE_HOSTS },
  });
}
